import type { ChatUiMessage } from '../../../lib/interfaces/Chat';

export const GOAL_CHECKLIST_ITEM_STATUSES = ['pending', 'active', 'partial', 'completed', 'deferred', 'blocked'] as const;
export type GoalChecklistItemStatus = typeof GOAL_CHECKLIST_ITEM_STATUSES[number];
export const GOAL_CHECKLIST_STATUSES = ['active', 'paused', 'completed', 'error'] as const;
export type GoalChecklistStatus = typeof GOAL_CHECKLIST_STATUSES[number];

export interface GoalChecklistEvidence { text: string; source: string; turnId: string | null; createdAt: string; learnerExcerpt?: string | null; }
export interface GoalChecklistItem {
  id: string; label: string; status: GoalChecklistItemStatus; optional?: boolean;
  /** Engines that know it emit `required`; older snapshots only carry `optional`. */
  required?: boolean;
  /** Admin detail (present when the engine emits it; older snapshots omit it). */
  description?: string | null; masteryCriteria?: string[]; attemptCount?: number; confidence?: number;
  latestEvidence?: GoalChecklistEvidence | null;
}
export interface GoalChecklistSnapshot {
  goalId: string; revision: number; title: string; status: GoalChecklistStatus; activeItemId: string | null;
  completedCount: number; totalCount: number; updatedAt: string; items: GoalChecklistItem[]; demo?: boolean;
  /** Mastery credit for the bar, when the engine reports a weighted figure. */
  progressPercentage?: number;
  /** Context label for that figure, e.g. `Mastery` when demonstration is
   * reachable or `Progress` when measurement is provisional. */
  masteryLabel?: string;
  /** Presented share of the goal's bound nodes. A different measure from
   * mastery: teacher activity over the goal's map, not learner state. */
  coveragePercentage?: number;
  /** Label for the coverage figure, e.g. `Material covered`. */
  coverageLabel?: string;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}
function nonEmptyString(value: unknown): value is string { return typeof value === 'string' && value.trim().length > 0; }

function itemDetail(item: Record<string, unknown>): Pick<GoalChecklistItem, 'description' | 'masteryCriteria' | 'attemptCount' | 'confidence' | 'latestEvidence'> {
  const detail: Pick<GoalChecklistItem, 'description' | 'masteryCriteria' | 'attemptCount' | 'confidence' | 'latestEvidence'> = {};
  if (nonEmptyString(item.description)) detail.description = item.description.trim();
  if (Array.isArray(item.masteryCriteria)) {
    const criteria = item.masteryCriteria.filter(nonEmptyString).map((entry) => entry.trim());
    if (criteria.length > 0) detail.masteryCriteria = criteria;
  }
  if (Number.isInteger(item.attemptCount) && (item.attemptCount as number) >= 0) detail.attemptCount = item.attemptCount as number;
  if (typeof item.confidence === 'number' && Number.isFinite(item.confidence)) detail.confidence = Math.min(1, Math.max(0, item.confidence));
  const evidence = record(item.latestEvidence);
  if (evidence && nonEmptyString(evidence.text)) {
    detail.latestEvidence = {
      text: evidence.text.trim(),
      source: nonEmptyString(evidence.source) ? evidence.source : 'unknown',
      turnId: nonEmptyString(evidence.turnId) ? evidence.turnId : null,
      createdAt: nonEmptyString(evidence.createdAt) ? evidence.createdAt : '',
      ...(nonEmptyString(evidence.learnerExcerpt) ? { learnerExcerpt: evidence.learnerExcerpt.trim() } : {}),
    };
  }
  return detail;
}

export function normalizeGoalChecklistSnapshot(value: unknown): GoalChecklistSnapshot | null {
  const input = record(value);
  if (!input || !nonEmptyString(input.goalId) || !nonEmptyString(input.title)) return null;
  if (!Number.isInteger(input.revision) || (input.revision as number) < 0) return null;
  if (!GOAL_CHECKLIST_STATUSES.includes(input.status as GoalChecklistStatus)) return null;
  if (!Number.isInteger(input.completedCount) || !Number.isInteger(input.totalCount)) return null;
  const completedCount = input.completedCount as number; const totalCount = input.totalCount as number;
  if (completedCount < 0 || totalCount < 0 || completedCount > totalCount) return null;
  if (!nonEmptyString(input.updatedAt) || Number.isNaN(Date.parse(input.updatedAt))) return null;
  if (input.activeItemId !== null && !nonEmptyString(input.activeItemId)) return null;
  if (!Array.isArray(input.items) || input.items.length !== totalCount) return null;
  const ids = new Set<string>(); const items: GoalChecklistItem[] = [];
  for (const rawItem of input.items) {
    const item = record(rawItem);
    if (!item || !nonEmptyString(item.id) || !nonEmptyString(item.label) || ids.has(item.id)) return null;
    if (!GOAL_CHECKLIST_ITEM_STATUSES.includes(item.status as GoalChecklistItemStatus)) return null;
    ids.add(item.id);
    items.push({
      id: item.id, label: item.label, status: item.status as GoalChecklistItemStatus, optional: item.optional === true || undefined,
      ...(typeof item.required === 'boolean' ? { required: item.required } : {}),
      ...itemDetail(item),
    });
  }
  const progressPercentage = typeof input.progressPercentage === 'number' && Number.isFinite(input.progressPercentage)
    ? Math.min(100, Math.max(0, input.progressPercentage))
    : undefined;
  const masteryLabel = nonEmptyString(input.masteryLabel) ? input.masteryLabel.trim() : undefined;
  const coveragePercentage = typeof input.coveragePercentage === 'number' && Number.isFinite(input.coveragePercentage)
    ? Math.min(100, Math.max(0, input.coveragePercentage))
    : undefined;
  const coverageLabel = nonEmptyString(input.coverageLabel) ? input.coverageLabel.trim() : undefined;
  return {
    goalId: input.goalId, revision: input.revision as number, title: input.title, status: input.status as GoalChecklistStatus,
    activeItemId: input.activeItemId as string | null, completedCount, totalCount, updatedAt: input.updatedAt, items,
    demo: input.demo === true || undefined,
    ...(progressPercentage === undefined ? {} : { progressPercentage }),
    ...(masteryLabel === undefined ? {} : { masteryLabel }),
    ...(coveragePercentage === undefined ? {} : { coveragePercentage }),
    ...(coverageLabel === undefined ? {} : { coverageLabel }),
  };
}

export function createEmptyGoalChecklistSnapshot(): GoalChecklistSnapshot {
  return {
    goalId: 'empty-goal-snapshot',
    revision: 0,
    title: 'No active goal',
    status: 'active',
    activeItemId: null,
    completedCount: 0,
    totalCount: 0,
    updatedAt: new Date().toISOString(),
    items: [],
  };
}
export const EMPTY_GOAL_CHECKLIST_SNAPSHOT: GoalChecklistSnapshot = createEmptyGoalChecklistSnapshot();

export function chooseNewerGoalChecklistSnapshot(current: GoalChecklistSnapshot | null, incoming: GoalChecklistSnapshot | null): GoalChecklistSnapshot | null {
  if (!incoming) return current; if (!current || current.goalId !== incoming.goalId) return incoming;
  return incoming.revision >= current.revision ? incoming : current;
}
export function latestGoalChecklistSnapshot(messages: ChatUiMessage[]): GoalChecklistSnapshot | null {
  let selected: GoalChecklistSnapshot | null = null;
  for (const message of messages) { const goal = record(message?.goal); const runtime = record(goal?.runtime); selected = chooseNewerGoalChecklistSnapshot(selected, normalizeGoalChecklistSnapshot(runtime?.checklist ?? goal?.checklist)); }
  return selected;
}
